/**
 * Adds the gift box or another upsell (the lifetime warranty) from the cart
 * upsell cards (snippets/cart-gift-box.liquid).
 *
 * The card sits in cart markup that the Section Rendering API replaces on
 * every cart change, so the click is delegated from the document rather than
 * bound to the button, and this file is loaded outside that markup.
 *
 * Adding goes through Shopify.actions.updateCart, like the necklace itself:
 * it fires the standard lines-update event, so the drawer re-renders with the
 * gift box as a line and the card drops out. Pages without Shopify.actions
 * fall back to the Ajax cart and a reload.
 */
// One add at a time per card: while a card's item is loading, more taps on
// that card are ignored; other cards still work. Tracked by variant id
// because the drawer re-render can replace the button, so its disabled state
// alone isn't enough.
const adding = new Set();

// Adds from different cards run one after another: two cart updates in
// flight at once lost the first (tapping the gift box then the warranty
// added only the warranty). A tapped card shows its spinner straight away
// and its add starts once the one before it has finished.
let queue = Promise.resolve();

document.addEventListener('click', (event) => {
  // A tap anywhere on an upsell card counts as its Add button.
  const card = event.target.closest('.cart-gift-box__card');
  const button = event.target.closest('[data-gift-box-add]') || (card && card.querySelector('[data-gift-box-add]'));
  if (!button) return;
  const variantId = button.dataset.variantId;
  if (adding.has(variantId) || button.disabled) {
    event.preventDefault();
    return;
  }
  adding.add(variantId);

  // One box per necklace in the cart, worked out in Liquid.
  const quantity = Math.max(1, Number(button.dataset.quantity) || 1);
  // A spinner in the button (the label stays, invisible, so the width holds)
  // until the drawer re-renders with the item.
  button.disabled = true;
  button.classList.add('is-loading');

  const restore = (err) => {
    console.error('[gift-box] add failed', err);
    adding.delete(variantId);
    button.disabled = false;
    button.classList.remove('is-loading');
  };

  const added = () => {
    // Shopify confirms before the drawer has re-rendered, so stay locked
    // until this card's button is replaced (up to 5s), or a quick second tap
    // on the still-visible card would add the item twice.
    const unlockedAt = Date.now() + 5000;
    const waitForRerender = () => {
      if (!document.contains(button) || Date.now() > unlockedAt) adding.delete(variantId);
      else setTimeout(waitForRerender, 100);
    };
    waitForRerender();
    if (window.posthog && typeof window.posthog.capture === 'function') {
      // gift_box_added, or e.g. warranty_added for the other upsell cards.
      const upsell = button.dataset.upsell || 'gift_box';
      window.posthog.capture(upsell + '_added', { quantity });
    }
  };

  // Resolves once this add has finished (either way), so the next queued
  // add can start.
  const run = () => {
    if (window.Shopify && window.Shopify.actions) {
      return window.Shopify.actions
        .updateCart({ lines: [{ merchandiseId: 'gid://shopify/ProductVariant/' + variantId, quantity }] })
        .then((result) => {
          if (result.userErrors && result.userErrors.length) throw new Error(result.userErrors[0].message);
          added();
        })
        .catch(restore);
    }

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity }] }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        added();
        window.location.reload();
      })
      .catch(restore);
  };
  queue = queue.then(run);
});
